import React, { createContext, use } from "react";
import { isSameDay, parseDate, today } from "@internationalized/date";
import { useSuspenseQuery } from "@tanstack/react-query";

import { staffPortalApi } from "../api";
import type { StaffingData } from "../types";

export interface StaffPin {
	staffId: string;
	pin: string;
}

export interface StaffMessage {
	id: string;
	senderId: string;
	senderName: string;
	recipientId: string;
	recipientName: string;
	content: string;
	timestamp: string;
	read: boolean;
}

export interface StaffNotification {
	id: string;
	staffId: string;
	title: string;
	message: string;
	type: "info" | "warning" | "error";
	timestamp: string;
	read: boolean;
	actionUrl?: string;
}

export interface StaffPortalData extends StaffingData {
	version: number;
}

export function useStaffPortalOverview({
	tenant,
	venueId,
	timeZone,
}: {
	tenant: string;
	venueId: string;
	timeZone: string;
}) {
	const staffPortalQuery = useSuspenseQuery({
		...staffPortalApi.getOptions(tenant, venueId),
	});

	const [token, setToken] = React.useState<string | undefined | null>(
		undefined,
	);

	const data = staffPortalQuery.data;

	const sync = staffPortalQuery.refetch;

	const fetch = sync;

	const todayShift = data.shifts
		.filter((shift) => {
			return (
				isSameDay(today(timeZone), parseDate(shift.date)) ||
				shift.status === "CheckedIn"
			);
		})
		.sort((a, b) => {
			// Sort by status - "CheckedIn" first, "Scheduled" second, then others alphabetically
			if (a.status === "CheckedIn" && b.status !== "CheckedIn") return -1;
			if (a.status !== "CheckedIn" && b.status === "CheckedIn") return 1;
			if (a.status === "Scheduled" && b.status !== "Scheduled") return -1;
			if (a.status !== "Scheduled" && b.status === "Scheduled") return 1;

			return a.status.localeCompare(b.status);
		});

	return {
		...data,
		fetch,
		sync,

		data,

		token,
		setToken,
		isAuthenticated: !!token,

		todayShift,
	};
}

export type StaffPortalContext = ReturnType<typeof useStaffPortalOverview>;

export const StaffPortalContext = createContext<StaffPortalContext | undefined>(
	undefined,
);

export const useStaffPortalContext = () => {
	const context = use(StaffPortalContext);
	if (!context) {
		throw new Error(
			"useStaffPortalContext must be used within a StaffPortalContextProvider",
		);
	}

	return context;
};
